import { JobStatus, modules } from '@veritone/glc-redux';

import { selectConfigEngines } from '../selectors';
import callGraphQLApi from '@helpers/callGraphQLApi';
import {
  NOOP,
  QUERY_ENGINE_RESULTS_SUCCESS,
  QUERY_ENGINE_RESULTS_FAILURE,
  CANCEL_JOB_FAILURE,
  CANCEL_JOB_SUCCESS,
  CREATE_ENGINE_JOB_SUCCESS,
  CREATE_ENGINE_JOB_FAILURE,
  CHECK_JOB_STATUS_FAILURE,
  CHECK_JOB_STATUS_SUCCESS,
  PROCESS_ENGINE_REQUEST_SUCCESS,
  // CREATE_ENGINE_JOB_VOICE_CONVERSION_SUCCESS,
  // CREATE_ENGINE_JOB_VOICE_CONVERSION_FAILURE,
} from './actions';
import { TDOId } from '@common-modules/universal/models/Brands';
import { selectIngestionOptions } from './selectors';
import { selectGlobalSettings } from '@common-modules/mediaDetails';
import { selectFeatureFlags } from '@common/user-permissions';
import { ClusterSimThresholdType } from '../../mediaDetails/models';
import { generateTasksAndRoutes } from '@helpers/engineJobHelper';
import { supportsSpeakerSeparation } from '@helpers/speakerSeparationLanguage';
import { UnknownAction } from 'redux-saga';

const { user } = modules;

/**
 * Load engine results from the JSON file.
 * @param {{ signedUri: string }} payload
 */
// export const getEngineResultsFromURIService = ({ signedUri }) => async (
//   dispatch,
//   getState
// ) => {
//   return await callApi({
//     signedUri,
//     actionTypes: [
//       NOOP,
//       GET_ENGINE_RESULTS_FROM_URI_SUCCESS,
//       GET_ENGINE_RESULTS_FROM_URI_FAILURE,
//     ],
//     dispatch,
//     getState,
//     bailout: undefined,
//   });
// };

/**
 * Query for the engine results to get the JSON data URI.
 * @param {{ tdoId: TDOId }} payload
 */
// export const queryEngineResultsRecordService = ({ tdoId }) => async (
//   dispatch,
//   getState
// ) => {
//   const query = `
//     query($id: ID!) {
//       temporalDataObject(id: $id) {
//         assets(type: "redacted-transcription-result" orderBy: createdDateTime limit: 1) {
//           records {
//             signedUri
//           }
//         }
//       }
//     }
//   `;

//   return await callGraphQLApi({
//     actionTypes: [
//       NOOP,
//       QUERY_ENGINE_RESULTS_RECORD_SUCCESS,
//       QUERY_ENGINE_RESULTS_RECORD_FAILURE,
//     ],
//     query,
//     variables: { id: tdoId },
//     dispatch,
//     getState,
//   });
// };

export interface QueryEngineResultsServiceResponse {
  engineResults: {
    records: {
      tdoId: TDOId;
      engineId: string;
      startOffsetMs: number;
      stopOffsetMs: number;
      jsondata: any;
    }[];
  };
}
export const queryEngineResultsService =
  ({
    tdoId,
    engines,
    mediaDuration,
  }: {
    tdoId: TDOId;
    engines: string[];
    mediaDuration: number;
  }) =>
  async (dispatch: (a: UnknownAction) => void, getState: () => any) => {
    const query = `
      query($tdoId: ID!, $engines: [ID!], $startOffset: Int, $stopOffset: Int) {
        engineResults(tdoId: $tdoId, engineIds: $engines, startOffsetMs: 0, stopOffsetMs: $stopOffset) {
          records {
            tdoId
            engineId
            startOffsetMs
            stopOffsetMs
            jsondata
          }
        }
      }
    `;
    return await callGraphQLApi<QueryEngineResultsServiceResponse>({
      actionTypes: [
        NOOP,
        QUERY_ENGINE_RESULTS_SUCCESS,
        QUERY_ENGINE_RESULTS_FAILURE,
      ],
      query,
      variables: { tdoId, engines, stopOffset: mediaDuration },
      dispatch,
      getState,
      bailout: undefined,
    });
  };

/**
 * Create an ingestion job.
 * @param {{ engineId: string; payload: { targetId: string; url: string } }} payload
 * {app: "redact"} Used for email notifications flow in Automate to filter tasks only from REDACT app. DONOT REMOVE.
 */
export const createEngineJobService =
  ({
    tdoId,
    tdoName,
    payload,
  }: {
    tdoId: TDOId;
    tdoName: string;
    payload: {
      // TODO: Type the acceptable fields for payload which are sent to the ingestion engine
      // targetId: string;
      // VE-30513: optional — the Redact upload saga omits `url` for A/V and
      // image uploads where the app owns the `original-media` write. The
      // GLC ingester falls back to the TDO's `media` then `original-media`
      // asset (`glc-ingestor/ingestion.py:1268-1286`).
      url?: string;
      urlHeaders?: string;
      faceDetectionThreshold?: number;
      legacyClustering?: boolean;
      clusterSimThreshold?: ClusterSimThresholdType;
      fileType?: string;
    };
  }) =>
  async (dispatch: (a: UnknownAction) => void, getState: () => any) => {
    // TODO: can we properly type getState?
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const state = getState();
    const {
      glcIngestionEngineId,
      fastChunkerEngineId,
      transcriptionEngines,
      transcriptionEngineId,
      transcriptionCategoryId,
      transcriptionEngineOptions,
      detectionEngineId,
      defaultClusterId,
      outputWriterEngineId,
      voiceMaskEngineId,
      speakerSeparationEngineId,
    } = selectConfigEngines(state);

    // const { email, organization } = user.selectUser(state);
    const { email } = user.selectUser(state);
    const {
      runHead,
      runPerson,
      runTranscription,
      runSpeakerSeparation,
      runVoiceMask,
      transcriptionLanguage,
    }: {
      runHead: boolean;
      runPerson: boolean;
      runTranscription: boolean;
      runSpeakerSeparation: boolean;
      runVoiceMask: boolean;
      transcriptionLanguage?: string;
    } = selectIngestionOptions(state);
    const {
      detectionRate,
      faceDetectionThreshold,
      videoType,
    }: {
      detectionRate: number;
      faceDetectionThreshold: number;
      videoType: string;
    } = selectGlobalSettings(state);
    const featureFlags = selectFeatureFlags(state);

    const ingestionPayload = {
      ...payload,
      user: email,
      // chunkSize: 10000000, deprecated!
      zeropadOffsetAudio: true,
    };

    const runDetection = payload.fileType?.includes('audio')
      ? false
      : runHead || runPerson;

    // Check if this is an image file - use Image DAG instead of video/audio DAG
    const isImage = payload.fileType?.startsWith('image/');

    const detectionOptions = {
      engineId: detectionEngineId,
      confidenceThreshold: Number((faceDetectionThreshold / 100).toFixed(2)),
      videoType,
      detectionRate,
      detectHead: runHead,
      detectNotepad: featureFlags.detectNotepads,
      detectCard: featureFlags.detectCards,
      detectPerson: runPerson,
      legacyClustering: payload.legacyClustering,
      clusterSimThreshold: payload.clusterSimThreshold,
    };

    // during initial development we used some more general veritone engines
    /*  const useAlternateImageIngestionEngines = false; 
    if (isImage && useAlternateImageIngestionEngines) {
      const [tasks, routes] =
      isImage && useAlternateImageIngestionEngines
        ? generateImageIngestionTasksAndRoutes({
            url: payload.url,
            si2EngineId: selectConfigEngines(state).si2EngineId,
            imageTranscodeEngineId:
              selectConfigEngines(state).imageTranscodeEngineId,
            thumbnailGeneratorEngineId:
              selectConfigEngines(state).thumbnailGeneratorEngineId,
            outputWriterEngineId,
            runDetection,
            ...(runDetection && {
              detectionOptions,
            }),
          })
    } */

    const [tasks, routes] = generateTasksAndRoutes({
      ingestionPayload,
      glcIngestionEngineId,
      outputWriterEngineId,
      fastChunkerEngineId,
      transcriptionEngineId:
        transcriptionEngines?.[transcriptionLanguage] ?? transcriptionEngineId,
      transcriptionCategoryId,
      transcriptionEngineOptions,
      // Still requires runTranscription: separation reads the transcript, so
      // without one there is nothing for it to group. The checkbox is what the
      // reviewer chose; the FilePicker checks it with Run Transcription but
      // lets them turn it back off.
      speakerSeparationEngineId:
        runTranscription &&
        runSpeakerSeparation &&
        !isImage &&
        featureFlags.enableSpeakerSeparation &&
        // one engine id, and Speechmatics ships one per language
        supportsSpeakerSeparation(transcriptionLanguage)
          ? speakerSeparationEngineId
          : undefined,
      runTranscription: runTranscription,
      runVoiceMask: runVoiceMask,
      runDetection,
      voiceMaskEngineId,
      voiceMask: featureFlags.voiceMask,
      ...(runDetection && {
        detectionOptions,
      }),
      isImage,
    });

    const input = {
      targetId: tdoId,
      clusterId: defaultClusterId || '',
      tasks,
      routes,
    };
    const query = `mutation($input: CreateJob!) {
      createJob(input: $input) {
        id
        targetId
        status
      }
    }`;

    return await callGraphQLApi<{
      createJob: {
        id: string;
        targetId: TDOId;
        status: string;
      };
    }>({
      actionTypes: [NOOP, CREATE_ENGINE_JOB_SUCCESS, CREATE_ENGINE_JOB_FAILURE],
      query,
      variables: { id: tdoId, tdoName, input },
      dispatch,
      getState,
    });
  };

/**
 * Whether an upload is a PDF, and therefore skips the converter.
 *
 * The single definition on purpose. The uploads saga tests it to decide whether
 * to attach the `media` asset itself, and this module tests it to decide whether
 * to put a converter in the DAG — two answers that MUST agree, because a job
 * with no converter and a TDO with no media asset is a document nothing can
 * render. It was two copies once; keep it one.
 */
export function isPdfMedia(
  fileType: string | undefined | null,
  name: string | undefined | null
): boolean {
  const normalized = (fileType ?? '').split(';')[0]?.trim().toLowerCase();
  return (
    normalized === 'application/pdf' || !!name?.toLowerCase().endsWith('.pdf')
  );
}

// DAG ioFolder reference ids for the document redaction job. The PDF converter
// writes its converted PDF to `pdfOutputFolder`, which routes into the OCR
// engine's `ocrInputFolder`; the OCR engine's `ocrOutputFolder` then routes into
// the PII engine's `piiInputFolder` when PII is requested.
const DOC_PDF_OUTPUT_FOLDER = 'pdfOutputFolder';
const DOC_OCR_INPUT_FOLDER = 'ocrInputFolder';
const DOC_OCR_OUTPUT_FOLDER = 'ocrOutputFolder';
const DOC_PII_INPUT_FOLDER = 'piiInputFolder';

interface DocTask {
  engineId: string;
  payload?: Record<string, unknown>;
  executionPreferences?: Record<string, unknown>;
  ioFolders?: Array<{
    referenceId: string;
    mode: 'chunk' | 'stream' | 'batch';
    type: 'input' | 'output';
  }>;
}

interface DocRoute {
  parentIoFolderReferenceId: string;
  childIoFolderReferenceId: string;
  options: Record<string, unknown>;
}

/**
 * Build the `tasks` + `routes` for a document redaction job.
 *
 * The DAG is a linear chain of three engines — PDF converter, Redact
 * Document OCR, document PII detection — assembled from whichever ones apply:
 *
 *   - the converter is present unless `skipPdfConverter` (VE-23391, PDF uploads:
 *     the file is already attached as the `media` asset, so there is nothing to
 *     convert),
 *   - OCR is present when `runOCR` (FilePicker "Run OCR on Documents") is set,
 *   - PII detection is present when `runPII` ("Run PII Detection") is set.
 *
 * The two cognition stages are gated INDEPENDENTLY. They were one flag once,
 * which made a searchable document something you could only get by also asking
 * for PII detection — the common case is wanting search on a scan and nothing
 * else. The dialog still couples them in the direction that matters (checking
 * PII checks OCR, because PII reads the text layer OCR writes), but that is a
 * default expressed in the UI, not a rule enforced here: a caller that asks for
 * PII alone gets PII alone, chained straight off the converter exactly as it
 * was before OCR existed.
 *
 * Chaining rules, applied per stage by its position rather than per
 * combination — which is what keeps this one code path instead of eight:
 *
 *   - a NON-ROOT task carries no file url: it resolves
 *     `primaryAsset(assetType: "media")` at run time. Both batch engines prefer
 *     a payload url when present, so passing one downstream would pin PII to the
 *     pre-OCR PDF. Root tasks do carry one — the converter always, and PII when
 *     it is root (converter skipped and no OCR stage), which is the shape that
 *     shipped before OCR existed,
 *   - a task with a parent gets an `input` ioFolder, a task with a child gets
 *     an `output` ioFolder, and each adjacent pair gets a route between them.
 *
 * An empty task list is a valid result (a PDF upload with neither stage asked
 * for); the caller turns it into a success with no engine work.
 */
export function buildDocumentRedactionTasksAndRoutes({
  pdfConverterEngineId,
  documentOcrEngineId,
  documentPIIDetectionEngineId,
  fileUrl,
  runPII,
  runOCR,
  skipPdfConverter = false,
}: {
  /**
   * Required only when the converter is in the chain. OPTIONAL, not `''`: the
   * document-cognition caller skips the converter and used to pass an empty
   * string with a comment saying nothing reads it — which types as a perfectly
   * good engine id, and is indistinguishable from a config value that came back
   * blank. That exact ambiguity is what made a blank `documentOcrEngineId`
   * silently invert the export's source choice (review gate BL-2), so it is not
   * a spelling worth keeping anywhere near this builder.
   */
  pdfConverterEngineId?: string;
  documentOcrEngineId?: string;
  documentPIIDetectionEngineId?: string;
  /** Required only when the converter is in the chain; see above. */
  fileUrl?: string;
  runPII: boolean;
  runOCR: boolean;
  skipPdfConverter?: boolean;
}): [DocTask[], DocRoute[]] {
  const chain: Array<DocTask & { inputFolder: string; outputFolder: string }> =
    [];

  if (!skipPdfConverter) {
    // An assertion, not a guard: the caller checks this and reports a proper
    // failure before getting here. It exists so the invariant is stated where
    // it is relied on, rather than the type being widened and the empty string
    // sailing through as an engine id.
    if (!pdfConverterEngineId) {
      throw new Error(
        'pdfConverterEngineId is required when the PDF converter is in the chain'
      );
    }
    chain.push({
      engineId: pdfConverterEngineId,
      // The converter is always first when present, so this is never read.
      inputFolder: '',
      outputFolder: DOC_PDF_OUTPUT_FOLDER,
      payload: { fileUrl },
    });
  }

  if (runOCR && documentOcrEngineId) {
    chain.push({
      engineId: documentOcrEngineId,
      inputFolder: DOC_OCR_INPUT_FOLDER,
      outputFolder: DOC_OCR_OUTPUT_FOLDER,
    });
  }

  if (runPII && documentPIIDetectionEngineId) {
    chain.push({
      engineId: documentPIIDetectionEngineId,
      inputFolder: DOC_PII_INPUT_FOLDER,
      // Nothing runs after PII detection.
      outputFolder: '',
      // Only reached when PII is the root — converter skipped and no OCR stage,
      // whether because the box is unchecked or no OCR engine is configured
      // (the rollback lever). That shape shipped with this payload before OCR
      // existed; keep it rather than change its behaviour. A non-root PII task
      // drops it (see below).
      ...(chain.length === 0 ? { payload: { url: fileUrl } } : {}),
    });
  }

  const tasks: DocTask[] = [];
  const routes: DocRoute[] = [];

  chain.forEach(({ inputFolder, outputFolder, ...link }, i) => {
    const parent = i > 0 ? chain[i - 1] : undefined;
    const child = chain[i + 1];

    const task: DocTask = { engineId: link.engineId };
    if (parent) {
      task.executionPreferences = { parentCompleteBeforeStarting: true };
    } else if (link.payload) {
      task.payload = link.payload;
    }

    const ioFolders: NonNullable<DocTask['ioFolders']> = [];
    if (parent) {
      ioFolders.push({
        referenceId: inputFolder,
        mode: 'chunk',
        type: 'input',
      });
    }
    if (child) {
      ioFolders.push({
        referenceId: outputFolder,
        mode: 'chunk',
        type: 'output',
      });
      routes.push({
        parentIoFolderReferenceId: outputFolder,
        childIoFolderReferenceId: child.inputFolder,
        options: {},
      });
    }
    if (ioFolders.length) {
      task.ioFolders = ioFolders;
    }

    tasks.push(task);
  });

  return [tasks, routes];
}

// TODO THIS NEEDS TO BE UPDATED TO USE NEW ENGINES!
/**
 * Create a document ingestion job using the pdf-converter engine for document file types (PDF, DOCX, EML, MSG, PPTX, XLSX, TXT, RTF).
 * The FilePicker's two document checkboxes chain their engines after the
 * converter independently: "Run OCR on Documents" → `runOCR` adds the Redact
 * Document OCR engine, "Run PII Detection" → `runPII` adds the document PII
 * detection engine.
 */
export const createDocumentRedactionJobService =
  ({
    tdoId,
    tdoName,
    payload,
  }: {
    tdoId: TDOId;
    tdoName: string;
    payload: {
      // VE-30513: typed optional to match the shared `ProcessEngineRequest`
      // shape, but required whenever the converter runs — checked below.
      url?: string;
      fileType?: string;
    };
  }) =>
  async (dispatch: (a: UnknownAction) => void, getState: () => any) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const state = getState();
    const {
      pdfConverterEngineId,
      documentOcrEngineId,
      documentPIIDetectionEngineId,
      defaultClusterId,
    } = selectConfigEngines(state);
    // The FilePicker's two document checkboxes. Each gates its own cognition
    // stage and neither gates the whole job: a non-PDF is still converted with
    // both of them off.
    const { runPII, runOCR }: { runPII: boolean; runOCR: boolean } =
      selectIngestionOptions(state);
    const { disableFastPdfIngestion } = selectFeatureFlags(state);

    // VE-23391: PDF uploads skip the converter, because the redact-app uploads
    // saga has already attached the file as the `media` asset. The
    // `disableFastPdfIngestion` flag forces them through the normal converter
    // DAG like every other document type.
    const skipPdfConverter =
      !disableFastPdfIngestion && isPdfMedia(payload.fileType, tdoName);

    // The converter engine is only required when we're actually going to run it.
    if (!skipPdfConverter && !pdfConverterEngineId) {
      dispatch(
        CREATE_ENGINE_JOB_FAILURE(
          new Error('pdfConverterEngineId is not configured'),
          { variables: { id: tdoId, tdoName } },
          true
        )
      );
      return;
    }

    // `url` is typed optional on the shared request shape (VE-30513 drops it
    // for A/V and image), but the converter reads it as its input. Fail the
    // job here, loudly, rather than send a DAG with no source.
    if (!skipPdfConverter && !payload.url) {
      dispatch(
        CREATE_ENGINE_JOB_FAILURE(
          new Error('document redaction job has no source url'),
          { variables: { id: tdoId, tdoName } },
          true
        )
      );
      return;
    }

    const [tasks, routes] = buildDocumentRedactionTasksAndRoutes({
      pdfConverterEngineId,
      documentOcrEngineId,
      documentPIIDetectionEngineId,
      fileUrl: payload.url,
      runPII,
      runOCR,
      skipPdfConverter,
    });

    // Nothing to run: a PDF upload (converter skipped) with neither cognition
    // stage asked for. Signal success so the ingestion pipeline
    // (startEngineJob) resolves; the success handler skips polling when there is
    // no `createJob`.
    if (tasks.length === 0) {
      // Resolve the ingestion pipeline: CREATE_ENGINE_JOB_SUCCESS unblocks
      // startEngineJob's race (its handler skips polling when `createJob` is
      // null), and PROCESS_ENGINE_REQUEST_SUCCESS reports the upload complete
      // (the media asset + recorded status are already in place).
      //
      // WHICH UPLOAD THIS IS, carried in meta. A null `createJob` has no
      // `targetId`, so this success was unattributable — and the redact-app
      // uploads saga, which waits for THIS TDO's job before it re-fetches the
      // folder, could not match it. The file then sat invisible for the full
      // ten-second timeout, on the one path where there was never going to be
      // a job to wait for. Several files can be uploading at once, so the id
      // has to be on the action rather than inferred.
      dispatch(
        CREATE_ENGINE_JOB_SUCCESS(
          { createJob: null } as unknown as {
            createJob: { id: string; targetId: TDOId; status: string };
          },
          // The rest of `SuccessMeta` describes a GraphQL call that was never
          // made — there is no job to create — so only `variables` carries
          // anything real.
          {
            variables: { id: tdoId, tdoName },
            operationName: undefined,
            query: '',
            _internalRequestId: '',
            _shouldTrackRequestsIndividually: false,
          }
        )
      );
      dispatch(PROCESS_ENGINE_REQUEST_SUCCESS({ tdoId, jobId: '' }));
      return;
    }

    const input = {
      targetId: tdoId,
      clusterId: defaultClusterId || '',
      tasks,
      ...(routes.length ? { routes } : {}),
    };
    const query = `mutation($input: CreateJob!) {
      createJob(input: $input) {
        id
        targetId
        status
      }
    }`;

    return await callGraphQLApi<{
      createJob: {
        id: string;
        targetId: TDOId;
        status: string;
      };
    }>({
      actionTypes: [NOOP, CREATE_ENGINE_JOB_SUCCESS, CREATE_ENGINE_JOB_FAILURE],
      query,
      variables: { id: tdoId, tdoName, input },
      dispatch,
      getState,
    });
  };

/** VMASKDAG not used currently 
export const createVoiceConversionJobService =
  ({ tdoId, tdoName }: { tdoId: TDOId; tdoName: string }) =>
  async (dispatch: (a: UnknownAction) => void, getState: () => any) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const state = getState();
    const { voiceMaskEngineId, defaultClusterId } = selectConfigEngines(state);
    const featureFlags = selectFeatureFlags(state);
    if (!featureFlags.voiceMask) {
      return;
    }

    const tasks = [
      {
        engineId: voiceMaskEngineId,
        executionPreferences: {
          parentCompleteBeforeStarting: true,
          priority: -15,
        },
        ioFolders: [
          {
            referenceId: 'voiceMaskInputFolder',
            mode: 'chunk',
            type: 'input',
          },
        ],
      },
    ];
    const input = {
      targetId: tdoId,
      clusterId: defaultClusterId || '',
      tasks,
    };

    const query = `mutation($input: CreateJob!) {
      createJob(input: $input) {
        id
        targetId
        status
      }
    }`;

    return await callGraphQLApi<{
      createJob: {
        id: string;
        targetId: TDOId;
        status: string;
      };
    }>({
      actionTypes: [
        NOOP,
        CREATE_ENGINE_JOB_VOICE_CONVERSION_SUCCESS,
        CREATE_ENGINE_JOB_VOICE_CONVERSION_FAILURE,
      ],
      query,
      variables: { id: tdoId, tdoName, input },
      dispatch,
      getState,
    });
  };
*/

/**
 * TODO THIS NEEDS TO BE UPDATED TO USE NEW ENGINES!
 * IDEALLY REUSE createEngineJobService and pass in trim params
 * Create an ingestion job.
 * @param {{ tdoId: TDOId; payload: { targetId: string; url: string }, runHead: boolean, runTranscription: boolean }} payload
 * {app: "redact"} Used for email notifications flow in Automate to filter tasks only from REDACT app. DONOT REMOVE.
 */
export const createEngineJobForTrimTDO =
  ({
    tdoId,
    payload,
    runHead,
    runTranscription,
  }: {
    tdoId: TDOId;
    payload: {
      targetId: string;
      url: string;
    };
    runHead: boolean;
    runTranscription: boolean;
  }) =>
  async (dispatch: (a: UnknownAction) => void, getState: () => any) => {
    console.log(
      ' tdoId, payload, runHead, runTranscription',
      tdoId,
      payload,
      runHead,
      runTranscription
    );
    // TODO: can we properly type getState?
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const state = getState();
    const {
      webstreamAdapterEngineId: engineId,
      transcriptionEngineId,
      transcriptionEngineOptions,
      detectionEngineId,
      streamIngestorEngineId,
      streamIngestorPlaybackSegmentCreatorId,
      defaultClusterId,
    } = selectConfigEngines(state);
    const email = user.selectUser(state).email;
    // const { runHead, runTranscription } = selectIngestionOptions(state);
    const { detectionRate, faceDetectionThreshold, videoType } =
      selectGlobalSettings(state);
    const query = trimJobV3f({
      defaultClusterId,
      streamIngestorPlaybackSegmentCreatorId,
      streamIngestorEngineId,
      detectionEngineId,
      transcriptionEngineId,
      transcriptionEngineOptions,
      faceDetectionThreshold,
      videoType,
      detectionRate,
      runHead,
      runTranscription,
    });

    return await callGraphQLApi<{
      createJob: {
        id: string;
        targetId: TDOId;
        status: string;
      };
    }>({
      actionTypes: [NOOP, CREATE_ENGINE_JOB_SUCCESS, CREATE_ENGINE_JOB_FAILURE],
      query,
      variables: {
        id: tdoId,
        engineId,
        payload: {
          ...payload,
          user: email,
        },
      },
      dispatch,
      getState,
    });
  };

const trimJobV3f = ({
  defaultClusterId,
  streamIngestorPlaybackSegmentCreatorId,
  streamIngestorEngineId,
  detectionEngineId,
  transcriptionEngineId,
  transcriptionEngineOptions,
  faceDetectionThreshold,
  videoType,
  detectionRate,
  runHead,
  runTranscription,
}: {
  defaultClusterId?: string;
  streamIngestorPlaybackSegmentCreatorId: string | undefined;
  streamIngestorEngineId: string | undefined;
  detectionEngineId: string;
  transcriptionEngineId: string;
  transcriptionEngineOptions: Record<string, unknown>;
  faceDetectionThreshold: number;
  videoType: string;
  detectionRate: number;
  runHead: boolean;
  runTranscription: boolean;
}) => `
    mutation ($id: ID!, $engineId: ID!, $payload: JSONData!){
    createJob(input: {
        targetId: $id,
        clusterId: "${defaultClusterId ?? ''}",
        tasks: [
          {
            engineId: $engineId,
            payload: $payload
          },
          {
            engineId: "${streamIngestorPlaybackSegmentCreatorId}" # playback
          },
          ${
            runHead
              ? `{
                  engineId: "${streamIngestorEngineId}",
                  payload: {
                    ffmpegTemplate: "video",
                    comment: "stream ingestor"
                    app: "redact"
                    chunkOverlap: 3
                    customFFMPEGProperties:{
                      chunkSizeInSeconds: "60"
                      outputChunkDuration: "1m"
                      chunkOverlapDuration: "3s"
                      chunkOverlap: "3s"
                      extractFramesPerSec: "1"
                    }
                  }
                },
                {
                  engineId: "${detectionEngineId}",
                  payload: {
                    confidenceThreshold: ${(
                      faceDetectionThreshold / 100
                    ).toFixed(2)},
                    videoType: "${videoType}",
                    stepSizeDetection: ${detectionRate},
                    app: "redact"
                  },
                },`
              : ''
          }
          ${
            runHead
              ? `{
                  engineId: "${detectionEngineId}",
                  payload: {
                    confidenceThreshold: ${(
                      faceDetectionThreshold / 100
                    ).toFixed(2)},
                    videoType: "${videoType}",
                    stepSizeDetection: ${detectionRate},
                    app: "redact"
                  },
                }`
              : ''
          }
          ${
            runTranscription
              ? `
              {
                engineId: "${streamIngestorEngineId}",
                payload: {
                  ffmpegTemplate: "audio",
                  comment: "stream ingestor"
                  app: "redact"
                  customFFMPEGProperties:{
                    outputChunkDuration: "2m"
                    chunkSizeInSeconds: "120"
                  }
                }
              },{
                engineId: "${transcriptionEngineId}",
                payload: ${
                  // wordTimeStamps is for the whisper transcription engine - harmless to include for other transcription engines
                  JSON.stringify(
                    Object.assign({}, transcriptionEngineOptions, {
                      diarise: 'false',
                      wordTimeStamps: true,
                      app: 'redact',
                    })
                  )
                }
              }`
              : ''
          }
        ]
      }) {
        id
        targetId
        status
      }
    }
  `;

/**
 * Cancel a running job.
 * @param {{ jobId: string }} action
 */
export interface CancelJobServiceResponse {
  cancelJob: {
    id: string;
    message: string;
  };
}
export const cancelJobService =
  ({ jobId }: { jobId: string }) =>
  async (dispatch: (a: UnknownAction) => void, getState: () => any) => {
    const query = `
      mutation cancelJob($id: ID!) {
        cancelJob(id: $id){
          id
          message
        }
      }
    `;

    return await callGraphQLApi({
      actionTypes: [NOOP, CANCEL_JOB_SUCCESS, CANCEL_JOB_FAILURE],
      query,
      variables: { id: jobId },
      dispatch,
      getState,
      bailout: undefined,
    });
  };

/**
 * Get the status of a job.
 * @param {{ jobId: string }} action
 */
export interface CheckJobStatusServiceResponse {
  job: {
    id: string;
    targetId: string;
    status: JobStatus;
  };
}
export const checkJobStatusService =
  ({ jobId }: { jobId: string }) =>
  async (dispatch: (a: UnknownAction) => void, getState: () => any) => {
    const query = `query($jobId: ID!) {
      job(id: $jobId) {
        id
        targetId
        status
      }
    }`;

    return await callGraphQLApi<CheckJobStatusServiceResponse>({
      actionTypes: [NOOP, CHECK_JOB_STATUS_SUCCESS, CHECK_JOB_STATUS_FAILURE],
      query,
      variables: { jobId },
      dispatch,
      getState,
      bailout: undefined,
    });
  };
